import {
  convertMoneyProtoToNumber,
  convertNumberToMoney,
} from "src/utils/noomContracts/moneyProto";
import { Plan } from "src/utils/redux/slices/plans";
import {
  getDaysFromBillingInterval,
  getTrialDurationFromBillingInterval,
} from "./noomContracts/billingProto";
import { BillingCyclePreviewProto } from "@noom/noom-contracts/noom_contracts/billing/bms/subscriptions/billing_cycle_preview";
import { BillingIntervalProto } from "@noom/noom-contracts/noom_contracts/billing/billing_interval";
import { captureException } from "./error";
import {
  ImmediateSwitch,
  Money,
  NextBillingCycleSwitch,
  OrderOperation,
  PurchaseOperation,
  SwitchOperation,
  SwitchSupplementaryInfo,
} from "gql/graphql";
import { MoneyProto } from "@noom/noom-contracts/noom_contracts/lib/money";
import {
  OrderOperationProto,
  PurchaseOperationProto,
  SwitchOperationProto,
  SwitchOperationProto_SwitchSupplementaryInfoProto,
} from "@noom/noom-contracts/noom_contracts/billing/bms/orders/order_operation";
import type { SubscriptionModificationReasonProto_Value } from "@noom/noom-contracts/noom_contracts/billing/subscription_modification_reason";

/**
 * Calculate weekly price from a total price and billing interval
 */
export function calculateWeeklyPrice(
  price?: MoneyProto | Money,
  billingInterval?: BillingIntervalProto
): MoneyProto | undefined {
  if (!billingInterval || price == null) {
    return undefined;
  }
  const days = getDaysFromBillingInterval(billingInterval);
  if (!days) return undefined;
  const priceValue = convertMoneyProtoToNumber(price);
  const weeklyPrice = Math.round((priceValue / days) * 7 * 100) / 100;
  return convertNumberToMoney(weeklyPrice, price.currencyCode || "USD");
}

/**
 * Calculate monthly price from a total price and billing interval
 */
export function calculateMonthlyPrice(
  price?: number | MoneyProto | Money,
  billingInterval?: BillingIntervalProto
): number | undefined {
  if (!billingInterval || price == null) {
    return undefined;
  }
  let priceValue: number;
  if (typeof price === "number") {
    priceValue = price;
  } else {
    priceValue = convertMoneyProtoToNumber(price);
  }
  let monthlyPrice: number | undefined;
  switch (billingInterval?.unit) {
    case "DAYS":
      monthlyPrice = (priceValue / billingInterval.count) * 30;
      break;
    case "WEEKS":
      monthlyPrice = (priceValue / billingInterval.count) * 4;
      break;
    case "MONTHS":
      monthlyPrice = priceValue / billingInterval.count;
      break;
    case "YEARS":
      monthlyPrice = priceValue / (billingInterval.count * 12);
      break;
    default:
      captureException(
        new Error(`Unsupported billing interval unit: ${billingInterval?.unit}`)
      );
      break;
  }
  if (monthlyPrice != null) {
    return Math.round(monthlyPrice * 100) / 100;
  }
  return undefined;
}

/**
 * Same logic as calculateMonthlyPrice, but returns a MoneyProto preserving the
 * currency from the input price.
 */
export function calculateMonthlyPriceInMoney(
  price?: MoneyProto | Money,
  billingInterval?: BillingIntervalProto
): MoneyProto | undefined {
  const monthlyPrice = calculateMonthlyPrice(price, billingInterval);
  if (monthlyPrice == null || !price) return undefined;

  return convertNumberToMoney(monthlyPrice, price.currencyCode || "USD");
}

export function convertCyclesToPlan({
  initialCycle,
  recurringCycle,
  pspPlanId,
  legacyNoomPlanId,
  trialSaleItemId,
}: {
  initialCycle?: BillingCyclePreviewProto;
  recurringCycle: BillingCyclePreviewProto;
  pspPlanId?: string;
  legacyNoomPlanId?: string;
  trialSaleItemId?: string;
}): Partial<Plan> {
  const recurringLineItem = recurringCycle?.invoice?.lineItems?.[0];
  const price = convertMoneyProtoToNumber(recurringCycle?.invoice?.totalAmount);
  const discounted_plan_price = convertMoneyProtoToNumber(
    recurringCycle?.invoice?.totalAmountBeforeTax
  );
  const plan: Partial<Plan> = {
    sale_item_id: recurringLineItem?.saleItemId,
    product_name: recurringLineItem?.saleItemWithTaxes?.skus?.[0]?.productName,
    braintree_id: pspPlanId,
    merchant_account: "noom",
    noom_plan_id: legacyNoomPlanId,
    currency: recurringCycle?.invoice?.totalAmount?.currencyCode || "USD",
    discounted_plan_price,
    billing_cycle_duration: recurringCycle.billingInterval.count,
    billing_cycle_duration_units: recurringCycle.billingInterval.unit,
    has_trial: !!initialCycle?.isTrial,
    plan_price:
      convertMoneyProtoToNumber(recurringCycle?.invoice?.totalBaseAmount) ?? 0,
    price,
    promo_discount_amount:
      convertMoneyProtoToNumber(recurringLineItem?.discountInfo?.promoAmount) ||
      0,
    sales_tax_amount:
      convertMoneyProtoToNumber(recurringCycle?.invoice?.totalTaxAmount) || 0,
    // An initial cycle is not necessarily a trial: plans can open with a
    // shortened full-price period, or with an initial term longer than the
    // recurring one, so the trial amounts have to be read off `isTrial`.
    trial_fee: initialCycle?.isTrial
      ? convertMoneyProtoToNumber(
          initialCycle?.invoice?.totalAmountBeforeTax
        ) ?? 0
      : 0,
    trial_duration: initialCycle?.isTrial
      ? getTrialDurationFromBillingInterval(initialCycle?.billingInterval)
      : null,
    one_source_tax_code: recurringLineItem?.taxEntry?.onesourceTaxCode,
    trial_fee_total_amount: initialCycle?.isTrial
      ? convertMoneyProtoToNumber(initialCycle?.invoice?.totalAmount) || 0
      : 0,
    trial_item_options: trialSaleItemId
      ? [
          {
            price:
              convertMoneyProtoToNumber(
                initialCycle?.invoice?.totalBaseAmount
              ) ?? 0,
            trial_sale_item_id: trialSaleItemId,
          },
        ]
      : undefined,
    regular_price:
      convertMoneyProtoToNumber(recurringCycle?.invoice?.totalBaseAmount) ?? 0,
    // Price calculations - using the gross price (with tax)
    monthly_price: calculateMonthlyPrice(price, recurringCycle.billingInterval),
    weekly_price: convertMoneyProtoToNumber(
      calculateWeeklyPrice(
        recurringCycle?.invoice?.totalAmount,
        recurringCycle.billingInterval
      )
    ),
    // Discounted prices - using the price before tax
    discounted_weekly_price: convertMoneyProtoToNumber(
      calculateWeeklyPrice(
        recurringCycle?.invoice?.totalAmountBeforeTax,
        recurringCycle.billingInterval
      )
    ),
  };
  return plan;
}

function convertGqlSupplementaryInfoToProto(
  info: SwitchSupplementaryInfo
): SwitchOperationProto_SwitchSupplementaryInfoProto {
  if (!info) return undefined;
  return {
    multiUserInfos: info.multiUserInfos.map((entry) => ({
      additionalUserEmail: entry.additionalUserEmail,
      additionalName: entry.additionalName,
    })),
  };
}

function convertGqlSwitchTypeToProto(
  switchType: ImmediateSwitch | NextBillingCycleSwitch
): SwitchOperationProto["switchType"] {
  // eslint-disable-next-line no-underscore-dangle
  switch (switchType.__typename) {
    case "ImmediateSwitch":
      return {
        immediateSwitch: {
          billingCycleBehavior: switchType.billingCycleBehavior,
          prorationBehavior: switchType.prorationBehavior,
          trialType: switchType.trialType,
        },
      };
    case "NextBillingCycleSwitch":
      return {
        nextBillingCycleSwitch: {
          revertBehavior: switchType.revertBehavior,
        },
      };
    default: {
      // eslint-disable-next-line no-underscore-dangle
      const name = (switchType as any).__typename;
      throw new Error(`Unknown switch type: ${name}`);
    }
  }
}

/**
 * Converts a GraphQL SwitchOperation into the proto shape expected by the
 * billing REST API.
 */
export function convertGqlSwitchOperationToProto({
  saleItemId,
  trialSaleItemId,
  subscriptionModificationReason,
  switchType,
  promoCode,
  shippingAddressId,
  supplementaryInfo,
}: SwitchOperation): SwitchOperationProto {
  return {
    saleItemId,
    trialSaleItemId,
    subscriptionModificationReason: (subscriptionModificationReason ??
      "UNSPECIFIED_REASON") as SubscriptionModificationReasonProto_Value,
    switchType: convertGqlSwitchTypeToProto(switchType),
    promoCode,
    shippingAddressId,
    supplementaryInfo: convertGqlSupplementaryInfoToProto(supplementaryInfo),
  };
}

export function convertGqlPurchaseOperationToProto(
  op: PurchaseOperation
): PurchaseOperationProto {
  return {
    saleItemId: op.saleItemId,
    trialSaleItemId: op.trialSaleItemId,
    promoCode: op.promoCode,
    dependentPurchases: [],
    metadata: { purchaseFlow: "WEB" },
  };
}

export function convertGqlOrderOperationToProto(
  op: OrderOperation
): OrderOperationProto {
  // eslint-disable-next-line no-underscore-dangle
  switch (op.__typename) {
    case "PurchaseOperation":
      return {
        purchaseOperation: convertGqlPurchaseOperationToProto(op),
      };
    case "SwitchOperation":
      return { switchOperation: convertGqlSwitchOperationToProto(op) };
    default: {
      // eslint-disable-next-line no-underscore-dangle
      const name = (op as any).__typename;
      throw new Error(`Unknown order operation type: ${name}`);
    }
  }
}
